import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import images from "../../../assets/images";
import { OnboardingArrowRightIcon } from "../../../assets/icon";
import { DotsIndicator } from "../../../components/onboarding/dots-indicator-onboarding/dots-indicator-onboarding";
import { usePreloadImages } from "../../../hooks/usePreloadImages";

interface Step15LessonOnboardingProps {
  handlePrev: () => void;
  handleNext: () => void;
}

export const Step15LessonOnboarding = ({
  handlePrev,
  handleNext,
}: Step15LessonOnboardingProps) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isLargeScreen = useMediaQuery("(min-width: 1369px)");
  const isCompactLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1368px)",
  );
  const img = images.LessonOnboardingDS15;

  const loaded = usePreloadImages([img]);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const checkSize = () => {
      if (contentRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const containerWidth = containerRef.current.clientWidth;
        const contentHeight = contentRef.current.scrollHeight;
        const contentWidth = contentRef.current.scrollWidth;

        const reservedBottomPx = isCompactLandscape ? 28 : 0;
        const effectiveContainerHeight = Math.max(
          0,
          containerHeight - reservedBottomPx,
        );

        const safetyMargin = isCompactLandscape ? 0.8 : 0.98;
        const heightScale = effectiveContainerHeight / contentHeight;
        const widthScale = containerWidth / contentWidth;
        let fittedScale = Math.min(heightScale, widthScale) * safetyMargin;

        if (isCompactLandscape && fittedScale > 0.8) {
          fittedScale = 0.8;
        }

        const minScale = isCompactLandscape ? 0.6 : isMobile ? 0.7 : 0.8;

        setScale(Math.max(Math.min(fittedScale, 1), minScale));
      }
    };

    checkSize();

    window.addEventListener("resize", checkSize);
    window.addEventListener("orientationchange", checkSize);

    const timer = setTimeout(checkSize, 300);

    return () => {
      window.removeEventListener("resize", checkSize);
      window.removeEventListener("orientationchange", checkSize);
      clearTimeout(timer);
    };
  }, [isCompactLandscape, loaded, isMobile]);

  if (!loaded) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100svh",
        width: "100%",
        overflow: "hidden",
        padding: 0,
        backgroundColor: "#fff",
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          paddingX: isCompactLandscape
            ? { xs: "12px", sm: "16px" }
            : { xs: "16px", sm: "24px" },
          maxWidth: "500px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isCompactLandscape
            ? { xs: "8px", sm: "12px" }
            : { xs: "16px", sm: "24px", md: "32px" },
          transform: isLargeScreen ? `scale(${scale * 0.88})` : `scale(${scale})`,
          transformOrigin: "center center",
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              width: isCompactLandscape
                ? { xs: "70px", sm: "84px" }
                : { xs: "100px", sm: "120px", md: "140px" },
              mb: { xs: 0, sm: 1 },
            }}
            component="img"
            src={images.OnboardingBotSmall}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: isCompactLandscape
                  ? { xs: "20px", sm: "24px" }
                  : { xs: "28px", sm: "32px", md: "40px" },
                fontWeight: 700,
                textAlign: "center",
                color: "#1F2937",
                lineHeight: { xs: 1.2, sm: 1.3 },
              }}
            >
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(to right, #8957CF, #28C9DC)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Track your progress
              </Box>
              {" - "}
              See your results
            </Typography>

            <Typography
              sx={{
                fontSize: isCompactLandscape
                  ? { xs: "11px", sm: "13px" }
                  : { xs: "14px", sm: "16px", md: "18px" },
                fontWeight: 600,
                textAlign: "center",
                color: "#84909F",
                marginTop: isCompactLandscape ? "6px" : "10px",
              }}
            >
              See your improvement over time with before/after lesson results and
              keep building momentum.
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            onClick={handleNext}
            sx={{
              fontSize: isCompactLandscape
                ? { xs: "14px", sm: "16px" }
                : { xs: "16px", sm: "18px" },
              background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "999px",
              px: isCompactLandscape
                ? { xs: "18px", sm: "24px" }
                : { xs: "24px", sm: "32px" },
              py: isCompactLandscape
                ? { xs: "8px", sm: "10px" }
                : { xs: "12px", sm: "16px" },
              minWidth: { xs: "120px", sm: "auto" },
              boxShadow: "0px 4px 10px rgba(137, 87, 207, 0.2)",
              "&:hover": {
                background: "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
              },
            }}
          >
            Start the lesson
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            component="img"
            src={img}
            sx={{
              width: "100%",
              maxWidth: isCompactLandscape
                ? { xs: "220px", sm: "260px", md: "300px" }
                : { xs: "260px", sm: "300px", md: "360px" },
              maxHeight: isCompactLandscape ? "40dvh" : { xs: "260px", sm: "320px", md: "360px" },
              objectFit: "contain",
            }}
          />
        </motion.div>

        <motion.div
          style={{ width: "100%" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              paddingX: { xs: "12px", sm: "20px" },
              marginTop: isCompactLandscape ? { xs: "6px", sm: "10px" } : { xs: "16px", sm: "20px", md: "20px" },
              marginBottom: isCompactLandscape
                ? { xs: "8px", sm: "10px" }
                : { xs: "16px", sm: "24px", md: "32px" },
            }}
          >
            <OnboardingArrowRightIcon
              style={{
                width: isMobile ? "24px" : "30px",
                cursor: "pointer",
              }}
              onClick={handlePrev}
            />
            <DotsIndicator total={15} activeIndex={14} />
            <Box style={{ width: isMobile ? "24px" : "30px" }} />
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

